import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, MediaRefSchema, nullable } from './common.js';
import { ContentCategorySchema, ContentStatusSchema, ContentTypeSchema } from './enums/content.js';
import { AdminPageQuerySchema, AppCursorQuerySchema, adminPage, appCursorPage, } from './pagination.js';
/*
 * Videos, reels, articles and news (A-03, A-04; M-03, M-06, M-07): 04-data-models.md § Content
 * and events, 05-api-specification.md `/v1/admin/contents*` and the app feeds. One `contents`
 * collection discriminated by `type` (ADR-0005); GALLERY (A-06, BACKEND-11) shares it.
 */
export const CONTENT_TITLE_MAX_LENGTH = 150;
export const CONTENT_SUMMARY_MAX_LENGTH = 500;
export const CONTENT_LOCATION_MAX_LENGTH = 120;
/** Sanitised article HTML (06 § Input handling); well inside the 1 MB body limit. */
export const CONTENT_BODY_MAX_LENGTH = 100_000;
export const CONTENT_TEAM_TAGS_MAX = 20;
export const CONTENT_SEARCH_MAX_LENGTH = 100;
/** A gallery holds 1 to 50 images, in display order (04-data-models.md § Content). */
export const GALLERY_IMAGES_MAX = 50;
/** The types the admin manages through `/v1/admin/contents`. */
export const EDITABLE_CONTENT_TYPES = ['VIDEO', 'REEL', 'ARTICLE', 'GALLERY'];
/** `externalUrl` (article "Read More", Q-14) is https only (06 § Input handling). */
export const HttpsUrlSchema = z
    .url({ protocol: /^https$/, hostname: z.regexes.domain })
    .max(2048, 'At most 2048 characters');
/** Statuses an admin sets directly; ARCHIVED is not used yet (unpublish returns to DRAFT). */
export const CONTENT_EDIT_STATUSES = ['DRAFT', 'PUBLISHED'];
export const ContentEditStatusSchema = z.enum(CONTENT_EDIT_STATUSES);
const TitleSchema = z.string().trim().min(1).max(CONTENT_TITLE_MAX_LENGTH);
const OptionalText = (max) => z.string().trim().max(max).nullable().optional();
const TeamIdsSchema = z
    .array(IdSchema)
    .max(CONTENT_TEAM_TAGS_MAX)
    .transform((ids) => [...new Set(ids.map((id) => id.toLowerCase()))]);
/** Ordered gallery images; the first one is the cover. Duplicates are a 400. */
const ImageMediaIdsSchema = z
    .array(IdSchema)
    .min(1, 'Add at least one image')
    .max(GALLERY_IMAGES_MAX, `At most ${GALLERY_IMAGES_MAX} images`)
    .transform((ids) => ids.map((id) => id.toLowerCase()))
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Each image only once' });
/** Admin row and detail: every field of every BACKEND-09 type (`null` where it does not apply). */
export const ContentSchema = z
    .object({
    id: IdSchema,
    type: ContentTypeSchema,
    category: ContentCategorySchema,
    title: z.string(),
    summary: nullable(z.string()),
    /** ARTICLE only: sanitised HTML. */
    body: nullable(z.string()),
    location: nullable(z.string()),
    /** ARTICLE only: "Read More" link (Q-14). */
    externalUrl: nullable(z.string()),
    cover: nullable(MediaRefSchema),
    /** VIDEO and REEL only. */
    video: nullable(MediaRefSchema),
    /** GALLERY only, in display order (empty for other types). */
    images: z.array(MediaRefSchema),
    /** GALLERY: the fixture the photos belong to. */
    eventId: nullable(IdSchema),
    teamIds: z.array(IdSchema),
    status: ContentStatusSchema,
    publishedAt: nullable(IsoDateTimeSchema),
    likeCount: z.number().int().min(0),
    shareCount: z.number().int().min(0),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .meta({ id: 'Content' });
/**
 * `GET /v1/admin/contents?type&category&status&q&from&to&page&limit`, newest first. `from` and
 * `to` are SAST calendar days on `publishedAt`; `q` searches title and summary (text index).
 */
export const ContentListQuerySchema = AdminPageQuerySchema.extend({
    type: ContentTypeSchema,
    category: ContentCategorySchema.optional(),
    status: ContentStatusSchema.optional(),
    q: z.string().trim().min(1).max(CONTENT_SEARCH_MAX_LENGTH).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
});
export const ContentPageSchema = adminPage(ContentSchema).meta({ id: 'ContentPage' });
/** Fields every type shares; GALLERY has no `coverMediaId` (its cover is the first image). */
const ContentCommonFields = {
    title: TitleSchema,
    summary: OptionalText(CONTENT_SUMMARY_MAX_LENGTH),
    category: ContentCategorySchema.optional(),
    location: OptionalText(CONTENT_LOCATION_MAX_LENGTH),
    teamIds: TeamIdsSchema.optional(),
    /** PUBLISHED needs every attached media READY (and a video for VIDEO and REEL). */
    status: ContentEditStatusSchema.optional(),
    /** Defaults to the moment of publishing; a future time keeps it out of the feeds until then. */
    publishedAt: z.iso.datetime({ offset: true }).optional(),
};
const ContentBaseFields = { ...ContentCommonFields, coverMediaId: IdSchema.nullable().optional() };
const VideoFields = { videoMediaId: IdSchema.nullable().optional() };
const ArticleFields = {
    body: z.string().max(CONTENT_BODY_MAX_LENGTH).nullable().optional(),
    externalUrl: HttpsUrlSchema.nullable().optional(),
};
const GalleryFields = {
    imageMediaIds: ImageMediaIdsSchema,
    eventId: IdSchema.nullable().optional(),
};
/** `POST /v1/admin/contents`: one shape per type (VIDEO and REEL take a video, ARTICLE a body). */
export const ContentCreateRequestSchema = z
    .discriminatedUnion('type', [
    z.strictObject({ type: z.literal('VIDEO'), ...ContentBaseFields, ...VideoFields }),
    z.strictObject({ type: z.literal('REEL'), ...ContentBaseFields, ...VideoFields }),
    z.strictObject({ type: z.literal('ARTICLE'), ...ContentBaseFields, ...ArticleFields }),
    z.strictObject({ type: z.literal('GALLERY'), ...ContentCommonFields, ...GalleryFields }),
])
    .meta({ id: 'ContentCreateRequest' });
/**
 * `PATCH /v1/admin/contents/{id}`: any subset; `null` clears a field. The type never changes;
 * fields of another type (a body on a video) are a 400. A gallery reorders its images by sending
 * the whole `imageMediaIds` list in the new order.
 */
export const ContentUpdateRequestSchema = z
    .strictObject({
    ...ContentBaseFields,
    title: TitleSchema.optional(),
    ...VideoFields,
    ...ArticleFields,
    imageMediaIds: ImageMediaIdsSchema.optional(),
    eventId: IdSchema.nullable().optional(),
})
    .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'Change at least one field',
})
    .meta({ id: 'ContentUpdateRequest' });
/**
 * Card in every app feed (`/videos`, `/articles`, `/reels`, `/gallery`, `/teams/{id}/videos`,
 * `/teams/{id}/articles`). The app reuses it for Home, Reels, Stadium and Team tabs.
 */
export const AppContentCardSchema = z
    .object({
    id: IdSchema,
    type: ContentTypeSchema,
    category: ContentCategorySchema,
    title: z.string(),
    summary: nullable(z.string()),
    location: nullable(z.string()),
    externalUrl: nullable(z.string()),
    /** GALLERY: the first image. */
    cover: nullable(MediaRefSchema),
    video: nullable(MediaRefSchema),
    /** GALLERY: every image in display order; empty for other types. */
    images: z.array(MediaRefSchema),
    /** GALLERY: the fixture the photos belong to, or `null`. */
    eventId: nullable(IdSchema),
    teamIds: z.array(IdSchema),
    publishedAt: IsoDateTimeSchema,
    likeCount: z.number().int().min(0),
    shareCount: z.number().int().min(0),
    /** The caller has liked it (BACKEND-12); always false for guests. */
    likedByMe: z.boolean(),
})
    .meta({ id: 'AppContentCard' });
/** `GET /v1/app/contents/{id}`: the card plus the article body. */
export const AppContentDetailSchema = AppContentCardSchema.extend({
    body: nullable(z.string()),
}).meta({ id: 'AppContentDetail' });
export const AppContentPageSchema = appCursorPage(AppContentCardSchema).meta({
    id: 'AppContentPage',
});
/** `GET /v1/app/videos?category&teamId&cursor&limit` (type VIDEO; reels have their own feed). */
export const AppVideoListQuerySchema = AppCursorQuerySchema.extend({
    category: ContentCategorySchema.optional(),
    teamId: IdSchema.optional(),
});
/** `GET /v1/app/articles?teamId&cursor&limit`. */
export const AppArticleListQuerySchema = AppCursorQuerySchema.extend({
    teamId: IdSchema.optional(),
});
/** `GET /v1/app/gallery?category&cursor&limit` (type GALLERY; the Stadium screen sends STADIUM). */
export const AppGalleryListQuerySchema = AppCursorQuerySchema.extend({
    category: ContentCategorySchema.optional(),
});
//# sourceMappingURL=content.js.map